/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import Avatar, {AvatarProps} from '@material-ui/core/Avatar';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {AvatarIconSize, Color, IconSize} from '../../types/types';
import {defineChildren, selectStyle} from './AvatarUtils';

export interface AvatarTypes extends AvatarProps {
  name?: string;
  size?: AvatarIconSize;
  notClickable?: boolean;
  isDevice?: boolean;
}
export type LabelProps = {
  label: string;
  style?: any;
};

const Initials = ({label, style}: LabelProps) => {
  if (label.substr(0, label.indexOf(' '))) {
    const str1 = label.substr(0, label.indexOf(' ')).substring(0, 1).toUpperCase();
    const str2 = label.substr(label.indexOf(' ')+1).substring(0, 1).toUpperCase();
    const initial = str1.concat(str2);
    return (
      <span className={style} style={{fontSize: '12px'}}>{initial}</span>
    );
  } else {
    return (
      <span className={style} style={{fontSize: '12px'}}>{label.substring(0, 2).toUpperCase()}</span>
    );
  }
};

const useStyles = makeStyles(theme => ({
  small : props => ({
    fontSize        : '12px !important',
    height          : `${props.size}px !important`,
    width           : `${props.size}px !important`,
    backgroundColor : dlsColor('grey5'),
    color           : dlsColor('grey3'),
    cursor          : props.notClickable ? 'inherit' : 'pointer'
  }),
  large : props => ({
    fontSize        : '34px !important',
    height          : `${props.size}px !important`,
    width           : `${props.size}px !important`,
    backgroundColor : dlsColor('paleBlue'),
    color           : dlsColor('aaBlue'),
    cursor          : props.notClickable ? 'inherit' : 'pointer'
  }),
  medium : props => ({
    fontSize        : '14px !important',
    height          : `${props.size}px !important`,
    width           : `${props.size}px !important`,
    backgroundColor : props.name ? dlsColor('grey5') : dlsColor('grey3'),
    color           : dlsColor('grey3'),
    cursor          : props.notClickable ? 'inherit' : 'pointer'
  }),
  custom : props => ({
    height : `${props.size}px !important`,
    width  : `${props.size}px !important`,
    cursor : props.notClickable ? 'inherit' : 'pointer'
  }),
  default : props => ({
    fontSize        : '14px !important',
    height          : '32px !important',
    width           : '32px !important',
    backgroundColor : props.name ? dlsColor('grey5') : dlsColor('grey3'),
    color           : dlsColor('grey1'),
    cursor          : props.notClickable ? 'inherit' : 'pointer'
  })
}));
/**
 * @Avatar
 *
 * Defines the Avatar component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 * import React from 'react';
 * import { Avatar } from '@ion/dls/src/components/avatar';
 *
 * function MyComponent {
 *   return (
 *     <React.Fragment>
 *      <>
 *        <Avatar name={username} size={AvatarIconSize.small} />
 *        <Avatar size={AvatarIconSize.medium} />
 *        <Avatar size={AvatarIconSize.large} src={imagePath} />
 *        <Avatar src={imagePath} size={AvatarIconSize.extraLarge} />
 *        <Avatar src={imagePath} size={AvatarIconSize.max} />
 *      </>
 *     </React.Fragment>
 *   );
 * }
 * ```
 *
 */
export const AvatarWrapper = ({name, size, notClickable, isDevice = false, ...props}: AvatarTypes) => {
  const avatarProps: AvatarTypes = {name: name, size: size, notClickable: notClickable};
  const classes = useStyles(avatarProps);
  const root = selectStyle(classes, AvatarIconSize, size);
  const children = defineChildren({AvatarIconSize, IconSize, Color, name, size, isDevice, Initials});
  return (
    <Avatar src={props.src} classes={{
      root : root
    }} {...props}>
      {children}
    </Avatar>);
};

export default AvatarWrapper;
